import { useEffect } from "react";

/**
 * JsonLd — injects a schema.org JSON-LD `<script>` tag into <head>.
 *
 * Why not react-helmet? We ship one <SEOHead>-style component per route so a
 * ~200-line dep isn't worth it. This mounts one <script type="application/ld+json">
 * with a stable `data-jsonld-id` and cleans it up on unmount so the next route
 * doesn't inherit stale schema.
 *
 * Usage:
 *   <JsonLd id="product-starter-kit" data={{ "@context": "https://schema.org", ... }} />
 */
export default function JsonLd({ id, data }) {
  useEffect(() => {
    if (!id || !data) return;
    const selector = `script[data-jsonld-id="${id}"]`;
    const existing = document.querySelector(selector);
    const el = existing || document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.jsonldId = id;
    try {
      el.textContent = JSON.stringify(data);
    } catch {
      // JSON.stringify can throw on circular refs — bail rather than crash.
      return undefined;
    }
    if (!existing) document.head.appendChild(el);
    return () => {
      // Only remove tags we mounted (leave any that were already there — e.g.,
      // Organization/WebSite from index.html).
      const still = document.querySelector(selector);
      if (still && still === el) still.remove();
    };
  }, [id, data]);
  return null;
}
